/**
 * SkillDetailPage — /agents/skills/:slug
 * Skill detail on the DetailPage template. Demo page: /profile (title, tabs, side rail). The tab is kept in the URL.
 */
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSkillOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { Skill } from '../../generated/api/types.gen.js';
import { Badge, Card, QueryBoundary } from '../../components/ui/index.js';
import { SafeMarkdown } from '../../components/ui/SafeMarkdown.js';
import { DetailPage } from '../../components/page/index.js';
import { EntityVersionHistory } from '../../components/entities/EntityVersionHistory.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import { sentenceCase } from '../../components/ui/FilterSelect.js';
import { FactList, FactRow } from './template-detail/detail-parts.js';

const TABS = [
  { id: 'procedure', label: 'Procedure' },
  { id: 'versions', label: 'Versions' },
] as const;

const BulletList: React.FC<{ title: string; items: readonly string[] }> = ({ title, items }) => (
  <Card title={title}>
    <ul className="list-inside list-disc space-y-1 text-sm text-body">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </Card>
);

const SkillRail: React.FC<{ skill: Skill }> = ({ skill }) => (
  <>
    <Card title="Runtime use">
      <FactList>
        <FactRow label="Used by" value={skill.usedByCount} />
        <FactRow label="Priority" value={skill.priority} />
      </FactList>
    </Card>
    <Card title="Identity">
      <FactList>
        <FactRow label="Category" value={sentenceCase(skill.category)} />
        <FactRow label="Module" value={sentenceCase(skill.moduleId)} />
        <FactRow label="Invoked when" value={skill.invocationDescription || 'Not described'} />
      </FactList>
    </Card>
    <Card title="Quality">
      <FactList>
        <FactRow label="Security" value={sentenceCase(skill.securityStatus)} />
        <FactRow label="Quality" value={sentenceCase(skill.qualityStatus)} />
        <FactRow label="Content hash" value={skill.contentHash ? skill.contentHash.slice(0, 12) : 'None'} />
      </FactList>
    </Card>
  </>
);

export const SkillDetailPage: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const query = useQuery({ ...getSkillOptions({ path: { slug } }), enabled: slug !== '' });
  const [tab, setTab] = useTabParam(['procedure', 'versions'] as const, 'procedure');

  return (
    <QueryBoundary query={query} label="the skill">
      {({ skill }) => (
        <DetailPage
          title={skill.name}
          subtitle={skill.description ?? undefined}
          breadcrumbs={[
            { label: 'Libraries', to: '/agents/libraries' },
            { label: 'Skills', to: '/agents/skills' },
            { label: skill.name },
          ]}
          badges={
            <>
              <Badge variant={skill.versionState === 'locked' ? 'success' : 'warning'}>
                {sentenceCase(skill.versionState)}
              </Badge>
              <Badge variant="neutral">Version {skill.version}</Badge>
              {!skill.enabled && <Badge variant="danger">Disabled</Badge>}
            </>
          }
          tabs={TABS}
          activeTab={tab}
          onTabChange={setTab}
          rail={<SkillRail skill={skill} />}
        >
          {tab === 'procedure' && (
            <>
              <Card title="Procedure">
                <div className="text-sm leading-6 text-body">
                  <SafeMarkdown>{skill.body || 'This skill has no procedure text.'}</SafeMarkdown>
                </div>
              </Card>
              {skill.verificationCriteria.length > 0 && (
                <BulletList title="Verification criteria" items={skill.verificationCriteria} />
              )}
              {skill.risks.length > 0 && <BulletList title="Risks" items={skill.risks} />}
            </>
          )}
          {tab === 'versions' && (
            <Card title="Version history">
              <EntityVersionHistory entityId={skill.id} />
            </Card>
          )}
        </DetailPage>
      )}
    </QueryBoundary>
  );
};
